import { Button, Grid, Typography, styled } from "@mui/material"
import { getFileFormat } from "@utils/file"
import { useEffect, useRef, useState } from "react"

type FileUploaderProps = {
  onUpload: (file: File) => void
  allowedFormats?: string[]
  name?: string
  validate?: (file: File) => string[] | Promise<string[]> // return array of error messages
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  overflow: "hidden",
  position: "absolute",
  whiteSpace: "nowrap",
})

const FileUploader = ({
  allowedFormats = [],
  name,
  onUpload,
  validate = () => [],
}: FileUploaderProps) => {
  const [uploadedFileName, setUploadedFileName] = useState("")
  const [isDragging, setIsDragging] = useState(false)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const dropAreaRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const dropAreaElement = dropAreaRef.current

    dropAreaElement?.addEventListener("dragover", handleDragOver)
    dropAreaElement?.addEventListener("drop", handleDrop)
    dropAreaElement?.addEventListener("dragleave", handleDragLeave)

    return () => {
      dropAreaElement?.removeEventListener("dragover", handleDragOver)
      dropAreaElement?.removeEventListener("drop", handleDrop)
      dropAreaElement?.removeEventListener("dragleave", handleDragLeave)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [])

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()

    setIsDragging(false)
  }

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()

    setIsDragging(true)
  }

  const handleDrop = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()

    handleUpload(event.dataTransfer?.files)
    setIsDragging(false)
  }

  const handleUpload = async (files: FileList | null | undefined) => {
    const uploadedFile = files?.item(0)
    if (!uploadedFile) return

    const fileFormat = getFileFormat(uploadedFile)
    const hasValidFormat = allowedFormats.some((format) => fileFormat === format)
    if (allowedFormats.length && !hasValidFormat) return

    setUploadedFileName(uploadedFile.name)

    const errors = await validate(uploadedFile)
    setValidationErrors(errors)
    if (errors.length > 0) return

    onUpload(uploadedFile)
  }

  return (
    <Grid
      ref={dropAreaRef}
      container
      direction="column"
      alignItems="center"
      gap={2}
      sx={{
        p: 1,
        borderStyle: "dashed",
        borderWidth: "1px",
        ...(isDragging && {
          backgroundColor: "#f3f3f3",
          borderWidth: "3px",
          borderColor: "primary",
        }),
      }}
    >
      <Grid item>
        <Typography>Drag & drop a file or click button below</Typography>
      </Grid>

      <Grid item>
        <Button variant="contained" component="label">
          Select a file
          <VisuallyHiddenInput
            type="file"
            accept={allowedFormats.join(",")}
            onChange={(event) => handleUpload(event.target.files)}
            name={name}
          />
        </Button>
      </Grid>

      {uploadedFileName && (
        <Grid item>
          <Typography>
            Selected file: <strong>{uploadedFileName}</strong>
          </Typography>
        </Grid>
      )}

      {validationErrors.length > 0 && (
        <Grid item>
          <Typography color="error" component="div">
            Validation errors:
            <ul>
              {validationErrors.map((errorMessage) => (
                <li key={errorMessage}>{errorMessage}</li>
              ))}
            </ul>
          </Typography>
        </Grid>
      )}

      <Grid item alignSelf="flex-end">
        <Typography>
          Allowed formats: <strong>{allowedFormats.join(", ")}</strong>
        </Typography>
      </Grid>
    </Grid>
  )
}

export default FileUploader
